/**
 * Home Screen Component
 * 
 * Dashboard-style home page displaying the main MFEs in a 2-column grid.
 * Unique to the web client - takes advantage of desktop screen real estate.
 * 
 * Features:
 * - 2-column grid layout on desktop
 * - Responsive: stacks to 1-column on tablet/mobile
 * - Each MFE in isolated panel with its own error boundary
 * - Click panel header to expand to full view
 * 
 * @module components/home/HomeScreen
 */

import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useProfile } from '@/contexts/ProfileContext';
import { useNavBadgesOptional } from '@/contexts/NavBadgesContext';
import { CommunicationBridge } from '@sequelae/lore-mfe-kit';
import type { NavBadgeSection } from '@/types/navBadges';
import { MFEPanel } from './MFEPanel';
import { LoreLoader } from '@sequelae/lore-core';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import './HomeScreen.css';

// MFE configurations for the home dashboard
// Uses the same icons as the sidebar navigation
// Order: Rewards (upper left), Community (upper right), LoreBot (lower left)
// Load with staggered delays to avoid race conditions and circular dependency issues
// Load order matters: Rewards first (0ms) to avoid "Cannot access 'g' before initialization"
// when shared scope is already touched by another remote. Then Community, LoreBot.
const HOME_MFES = [
  {
    name: 'Rewards',
    title: 'Rewards',
    iconOutline: '/icons/tabs/rewards_outline.svg',
    iconSolid: '/icons/tabs/rewards_solid.svg',
    path: '/rewards',
    delayMs: 0, // Load first to avoid shared-scope init order issues
  },
  {
    name: 'Community',
    title: 'Community',
    iconOutline: '/icons/tabs/community_outline.svg',
    iconSolid: '/icons/tabs/community_solid.svg',
    path: '/community',
    delayMs: 500,
  },
  {
    name: 'LoreBot',
    title: 'LoreBot',
    iconOutline: '/icons/tabs/lorebot_outline.svg',
    iconSolid: '/icons/tabs/lorebot_solid.svg',
    path: '/lorebot',
    delayMs: 1500,
  },
];

export const HomeScreen: React.FC = () => {
  const location = useLocation();
  const { user } = useAuth();
  const { profile } = useProfile();
  const navBadges = useNavBadgesOptional();
  const [loadedMfes, setLoadedMfes] = useState<Set<string>>(new Set());
  // Always fetch fresh token from CommunicationBridge
  const token = CommunicationBridge.getAccessToken();

  /** Map MFE name to nav-badge section key (for home panels only) */
  const mfeNameToSection = (name: string): NavBadgeSection | null => {
    switch (name) {
      case 'Rewards': return 'rewards';
      case 'Community': return 'community';
      case 'LoreBot': return 'lorebot';
      default: return null;
    }
  };

  // Staggered MFE loading to avoid race conditions and circular dependency issues
  useEffect(() => {
    if (location.pathname !== '/home') {
      setLoadedMfes(new Set());
      return;
    }

    // Schedule each MFE to load at its specified delay
    const timeouts: NodeJS.Timeout[] = [];

    HOME_MFES.forEach((mfe) => {
      const timeout = setTimeout(() => {
        setLoadedMfes(prev => new Set([...prev, mfe.name]));
      }, (mfe as any).delayMs || 0);

      timeouts.push(timeout);
    });

    return () => {
      timeouts.forEach(t => clearTimeout(t));
    };
  }, [location.pathname]);

  // Prevent auto-focus and unwanted scrolling (releases after 3 seconds or user interaction)
  useEffect(() => {
    if (location.pathname === '/home') {
      let isProtectionActive = true;
      const mainContent = document.querySelector('.main-content') as HTMLElement;

      const scrollToTop = () => {
        if (mainContent) mainContent.scrollTop = 0;
        window.scrollTo(0, 0);
      };

      const handleFocus = (e: FocusEvent) => {
        const target = e.target as HTMLElement;
        if (isProtectionActive && (target?.tagName === 'INPUT' || target?.tagName === 'TEXTAREA')) {
          e.preventDefault();
          e.stopImmediatePropagation();
          target.blur();
          scrollToTop();
        }
      };

      const handleScroll = (e: Event) => {
        if (!isProtectionActive || !mainContent) return;
        if (e.target !== mainContent) return;
        if (mainContent.scrollTop > 0) scrollToTop();
      };

      const disableProtection = () => {
        isProtectionActive = false;
      };

      scrollToTop();

      document.addEventListener('focusin', handleFocus, true);
      document.addEventListener('focus', handleFocus, true);
      if (mainContent) {
        mainContent.addEventListener('scroll', handleScroll, { passive: false });
      }

      // Auto-unlock after 3 seconds OR on user interaction (whichever comes first)
      const timer = setTimeout(disableProtection, 3000);
      document.addEventListener('mousedown', disableProtection, { once: true });
      document.addEventListener('touchstart', disableProtection, { once: true, passive: true });

      return () => {
        clearTimeout(timer);
        document.removeEventListener('focusin', handleFocus, true);
        document.removeEventListener('focus', handleFocus, true);
        if (mainContent) {
          mainContent.removeEventListener('scroll', handleScroll);
        }
        document.removeEventListener('mousedown', disableProtection);
        document.removeEventListener('touchstart', disableProtection);
      };
    }
  }, [location.pathname]);

  // When panel iframes navigate to /streaks, redirect the top window. Do NOT redirect on /rewards so tab clicks in MFEs don't take the user to full /rewards.
  // Redirect to /rewards only happens from index.html when the coin icon is explicitly clicked.
  useEffect(() => {
    if (location.pathname !== '/home') return;
    const interval = setInterval(() => {
      const iframes = document.querySelectorAll<HTMLIFrameElement>(
        '.mfe-panel-iframe-el--rewards'
      );
      for (let i = 0; i < iframes.length; i++) {
        try {
          const win = iframes[i].contentWindow;
          if (!win || win === window) continue;
          const iframePath = win.location.pathname;
          if (iframePath === '/streaks') {
            window.location.href = '/streaks';
            return;
          }
        } catch {
          // cross-origin or not loaded
        }
      }
    }, 16); // ~1 frame so redirect feels immediate once iframe navigates
    return () => clearInterval(interval);
  }, [location.pathname]);

  const handlePanelClick = (path: string) => {
    // Use hard refresh for MFE routes to prevent state pollution between MFEs
    window.location.href = path;
  };

  // Use lore_user_name from profile - NO email fallback (privacy)
  const displayName = profile?.lore_user_name || '';

  return (
    <div className="home-screen">
      {/* Header */}
      <div className="home-header">
        <div className="home-header-content">
          <HomeOutlinedIcon className="home-header-icon" />
          <h1 className="home-title">Welcome{displayName ? `, ${displayName}` : ''}!</h1>
        </div>
        <p className="home-subtitle">Your overview at a glance</p>
      </div>

      {/* MFE Grid */}
      <div className="home-grid">
        {HOME_MFES.map((mfe) => {
          // Show placeholder until this specific MFE is ready to load
          if (!loadedMfes.has(mfe.name)) {
            return (
              <div key={mfe.name} className="mfe-panel">
                <div className="mfe-panel-header">
                  <div className="mfe-panel-title-area">
                    <img src={mfe.iconSolid} alt={mfe.title} className="mfe-panel-icon" />
                    <h3 className="mfe-panel-title">{mfe.title}</h3>
                  </div>
                </div>
                <div className="mfe-panel-content">
                  <div className="mfe-panel-loading">
                    <LoreLoader size={40} />
                    <p style={{ marginTop: '12px', fontSize: '13px', color: '#666' }}>
                      Loading...
                    </p>
                  </div>
                </div>
              </div>
            );
          }

          const section = mfeNameToSection(mfe.name);
          return (
            <MFEPanel
              key={mfe.name}
              mfeName={mfe.name}
              title={mfe.title}
              iconSrc={mfe.iconSolid}
              accessToken={token || ''}
              refUserId={user?.id || ''}
              onExpand={() => handlePanelClick(mfe.path)}
              initialPath={mfe.name === 'Community' ? '/' : undefined}
              showBadge={Boolean(section && navBadges?.badges[section])}
            />
          );
        })}
      </div>
    </div>
  );
};

